import mixpanel from "mixpanel-browser";

import { ROLE_TYPES } from "../enums/Roles";
import { MIXPANEL_EVENTS } from "../constants/MixPanel";
import { getUserDataFromToken } from "../utils/AuthUtils";

const {
  NODE_ENV,
  REACT_APP_MIXPANEL_TOKEN,
  REACT_APP_MIXPANEL_RECORD_SESSIONS_PERCENT,
} = process.env;

let initialized = false;

const shouldInit = (role) => {
  const currentRole = role || localStorage.getItem("role");
  return currentRole === ROLE_TYPES.RESTAURANT_DRAFT;
};

const initMixpanel = (role) => {
  try {
    if (!shouldInit(role)) {
      if (initialized) {
        mixpanel.reset();
        initialized = false;
      }

      return false;
    }

    if (!initialized) {
      mixpanel.init(REACT_APP_MIXPANEL_TOKEN, {
        debug: NODE_ENV === "development",
        track_pageview: "url-with-path-and-query-string", // "full-url" logs everything, be caution
        record_sessions_percent:
          parseInt(REACT_APP_MIXPANEL_RECORD_SESSIONS_PERCENT) ?? 0, // % of the sessions to be recorded
        record_heatmap_data: false, // If enabled, it will record a lot of Element Clicks.
      });
      initialized = true;
    }

    return true;
  } catch (error) {
    console.error("MIXPANEL: Error initializing Mixpanel ", error);
    return false;
  }
};

const addLocationToUser = (location) => {
  try {
    if (!location) return;
    const { _id, role } = getUserDataFromToken();
    if (!_id || !initMixpanel(role)) return;
    const { street, region, country, state, city, restaurant_location } =
      location;

    mixpanel.register({
      restaurant_region: region || "null",
      ...(street && { restaurant_address: street }),
      ...(country && { restaurant_country: country }),
      ...(state && { restaurant_state: state }),
      ...(city && { restaurant_city: city }),
      ...(restaurant_location && { restaurant_location }),
    });

    mixpanel.people.set({
      restaurant_region: region || "null",
      ...(street && { restaurant_address: street }),
      ...(country && { restaurant_country: country }),
      ...(state && { restaurant_state: state }),
      ...(city && { restaurant_city: city }),
      ...(restaurant_location && { restaurant_location }),
    });
  } catch (error) {
    console.error("MIXPANEL: Error adding location to user ", error);
  }
};

const addRestaurantToUser = (restaurantName) => {
  try {
    mixpanel.register({
      ...(restaurantName && { restaurant_name: restaurantName }),
    });

    mixpanel.people.set({
      ...(restaurantName && { restaurant_name: restaurantName }),
    });
  } catch (error) {
    console.error("MIXPANEL: Error adding restaurant to user ", error);
  }
};

const identifyUser = async (user = {}, firstTime = false) => {
  try {
    const { _id, email, role, firstName, lastName, restaurant } = user;
    if (!_id || !initMixpanel(role)) return;

    mixpanel.identify(_id);

    if (firstTime) {
      mixpanel.track(MIXPANEL_EVENTS.BEGIN_REGISTER_CLICK, {
        user_id: _id,
        user_email: email,
        user_role: role,
        user_first_name: firstName,
      });
    }

    mixpanel.register({
      ...(email && { user_email: email }),
      ...(role && { user_role: role }),
      ...(firstName && { user_first_name: firstName }),
      ...(lastName && { user_last_name: lastName }),
      ...(restaurant && { user_restaurant_id: restaurant }),
    });

    mixpanel.people.set({
      $name: `${firstName} ${lastName}`,
      $email: email,
      role: role,
      first_name: firstName,
      last_name: lastName,
      user_restaurant_id: restaurant,
      $onesignal_user_id: _id,
    });
  } catch (error) {
    console.error("MIXPANEL: Error identifying user ", error);
  }
};

const trackEvent = (event, properties = {}) => {
  try {
    if (!initMixpanel()) return;
    mixpanel.track(event, properties);
  } catch (error) {
    console.error("MIXPANEL: Error tracking event ", error);
  }
};

const reset = () => {
  try {
    if (!initialized) return;
    mixpanel.reset();
    initialized = false;
  } catch (error) {
    console.error("MIXPANEL: Error resetting Mixpanel ", error);
  }
};

export {
  identifyUser,
  trackEvent,
  reset,
  addLocationToUser,
  addRestaurantToUser,
};
